import * as React from 'react';
// eslint-disable-next-line no-restricted-imports
import { Transition } from 'react-transition-group';

// Common Dependencies
import { Box } from '@leagueplatform/genesis-core';
import { usePrefersReducedMotion } from '@leagueplatform/web-common';

// Hooks

// Common Components

// Local Components

// Constants
import {
  TRANSITIONS,
  TRANSITION_DURATION_MS,
} from '../constants/multi-step-footer-constants';

// Types
interface MultiStepSlideProps {
  gap?: string;
  slideIndex: number;
  activeIndex: number;
}

// Styles
const slideTransition = `transform ${TRANSITIONS.PAGE}, opacity ${TRANSITIONS.PAGE}`;
const reducedMotionSlideTransition = `opacity ${TRANSITIONS.PAGE}`;
const reducedMotionSlideStyles = {
  insetInlineStart: 0,
  transform: 'none',
  transition: reducedMotionSlideTransition,
};

/**
 * This hook determines each slide's transitionable styles based on the users preference for reduced motion
 */
const useSlideTransitionStyles = ({
  slideIndex,
  gap,
  activeIndex,
}: MultiStepSlideProps) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const slideStyles = React.useMemo(
    () => ({
      insetInlineStart: `calc(${slideIndex} * ${gap})`,
      transform: `translateX(calc(-${gap} * ${activeIndex}))`,
      transition: slideTransition,
    }),
    [slideIndex, gap, activeIndex],
  );

  return React.useMemo(
    () => (prefersReducedMotion ? reducedMotionSlideStyles : slideStyles),
    [prefersReducedMotion, slideStyles],
  );
};

export const MultiStepSlide: React.FC<
  React.PropsWithChildren<MultiStepSlideProps>
> = ({ children, gap = '100%', slideIndex, activeIndex }) => {
  const slideTransitionStyles = useSlideTransitionStyles({
    gap,
    slideIndex,
    activeIndex,
  });
  const nodeRef = React.useRef<HTMLDivElement>(null);

  return (
    <Box
      aria-hidden={activeIndex !== slideIndex}
      css={{
        height: '100%',
        width: '100%',
        position: 'absolute',
        top: 0,
        opacity: Number(activeIndex === slideIndex),
        pointerEvents: activeIndex === slideIndex ? 'auto' : 'none',
        overflowY: 'auto',
        ...slideTransitionStyles,
      }}
      className="multi-step-slide"
    >
      <Transition
        nodeRef={nodeRef}
        in={activeIndex === slideIndex}
        timeout={TRANSITION_DURATION_MS.XLONG}
        unmountOnExit
      >
        {React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement, { ref: nodeRef })
          : children}
      </Transition>
    </Box>
  );
};

export const MultiStepSlider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => (
  <Box
    css={{
      height: '100%',
      width: '100%',
      position: 'relative',
      display: 'flex',
    }}
    className="multi-step-slider"
  >
    {children}
  </Box>
);
